// Betrieb › Löschnachweise (h-umzug).
//
// Für jedes endgültig gelöschte Radio: wann, wer die Löschung vorgemerkt hat,
// was je Tabelle entfernt wurde, wie viele Dateien und Bytes, dass beim
// Nachzählen nichts mehr übrig war und wie viele Protokolleinträge
// pseudonymisiert wurden. Das ist der Beleg gegenüber dem Radio (DSGVO Art. 17).

import { useEffect, useState } from "react";
import { umzugApi } from "../../api";
import { useAuth } from "../../auth/AuthProvider";
import { errorText } from "../../components/streaming-ui";
import { Meldungen, Sym } from "../../components/social-ui";
import { Badge, Callout, Card, EmptyState, Page, PageHeader, Skeleton } from "../../components/ui";
import { bytesText, datumText, zahlText } from "../../components/hosting-ui";
import type { Loeschnachweis } from "../../types";
import { NurBetreiber } from "./NurBetreiber";

export function LoeschnachweisePage(): JSX.Element {
  const { user } = useAuth();
  const [daten, setDaten] = useState<{ nachweise: Loeschnachweis[]; loeschen_aktiv: boolean } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (user?.role !== "super_admin") return;
    umzugApi.loeschnachweise().then(setDaten).catch((err) => setFehler(errorText(err)));
  }, [user?.role]);

  if (user?.role !== "super_admin") return <NurBetreiber titel="Löschnachweise" />;

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Betrieb"
        title="Löschnachweise"
        description="Beleg für jedes endgültig gelöschte Radio: was je Tabelle entfernt wurde, Dateien und Bytes, und dass danach nichts mehr übrig war."
      />
      <Meldungen error={fehler} />
      {daten && !daten.loeschen_aktiv && (
        <Callout tone="info" title="Endgültiges Löschen ist ausgeschaltet" className="mb-5">
          Zum Löschen vorgemerkte Radios bleiben gesperrt stehen, bis <code className="font-mono">HOSTING_LOESCHEN_AKTIV=true</code> gesetzt ist.
        </Callout>
      )}
      {!daten && !fehler && <Skeleton className="h-48" />}
      {daten && daten.nachweise.length === 0 && (
        <div className="card p-2">
          <EmptyState icon={<Sym name="shield" className="h-5 w-5" />} title="Noch kein Radio endgültig gelöscht." description="Nach Ablauf der 30 Tage Karenz entsteht hier für jedes gelöschte Radio ein Nachweis." />
        </div>
      )}
      <div className="space-y-4">
        {daten?.nachweise.map((n) => <Nachweis key={n.id} n={n} />)}
      </div>
    </Page>
  );
}

function Nachweis({ n }: { n: Loeschnachweis }): JSX.Element {
  const rest = Object.entries(n.rest ?? {});
  const vollstaendig = rest.length === 0 && (n.fehler ?? []).length === 0;
  const tabellen = Object.entries(n.zeilen ?? {}).sort((a, b) => b[1] - a[1]);
  return (
    <Card
      title={<span className="inline-flex flex-wrap items-center gap-2">{n.radio_name} <code className="font-mono text-xs font-normal text-muted">{n.radio_slug}</code></span>}
      description={`Gelöscht ${datumText(n.geloescht_at, true)}${n.vorgemerkt_von_name ? ` · vorgemerkt von ${n.vorgemerkt_von_name}${n.vorgemerkt_at ? ` am ${datumText(n.vorgemerkt_at)}` : ""}` : ""}`}
      actions={vollstaendig ? <Badge tone="success" dot>vollständig</Badge> : <Badge tone="warning" dot>mit Hinweisen</Badge>}
    >
      {n.grund && <p className="mb-3 text-sm text-secondary">Grund: <b className="font-medium text-primary">{n.grund}</b></p>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Wert label="Datensätze" wert={zahlText(n.zeilen_gesamt)} />
        <Wert label="Tabellen" wert={zahlText(tabellen.length)} />
        <Wert label="Dateien" wert={`${zahlText(n.dateien)} · ${bytesText(n.bytes)}`} />
        <Wert label="Protokoll pseudonymisiert" wert={zahlText(n.protokoll_pseudonymisiert)} />
      </div>
      <p className="mt-3 flex items-start gap-2 text-sm text-secondary">
        <Sym name={rest.length === 0 ? "check" : "alert"} className={`mt-0.5 h-4 w-4 flex-shrink-0 ${rest.length === 0 ? "text-emerald-500" : "text-amber-500"}`} />
        {rest.length === 0
          ? "Nachgezählt: In keiner Tabelle hing danach noch etwas an diesem Radio."
          : `Beim Nachzählen übrig (danach per Kaskade entfernt): ${rest.map(([t, c]) => `${t} ${c}`).join(", ")}`}
      </p>
      {(n.fehler ?? []).length > 0 && (
        <Callout tone="warning" className="mt-3">{n.fehler.join(" · ")}</Callout>
      )}
      <details className="mt-3 text-sm">
        <summary className="cursor-pointer text-secondary">Zeilen je Tabelle und Ordner</summary>
        <ul className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          {tabellen.map(([t, c]) => (
            <li key={t} className="flex justify-between gap-3 font-mono text-xs"><span className="truncate">{t}</span><span className="tabular-nums">{zahlText(c)}</span></li>
          ))}
        </ul>
        {(n.ordner ?? []).length > 0 && (
          <ul className="mt-3 space-y-0.5 break-all font-mono text-xs text-muted">{n.ordner.map((o) => <li key={o}>{o}</li>)}</ul>
        )}
        {n.suite && <p className="mt-3 text-xs text-muted">Suite {n.suite.version}{n.suite.commit ? ` (${n.suite.commit.slice(0, 10)})` : ""} · Nachweis {n.id}</p>}
      </details>
    </Card>
  );
}

function Wert({ label, wert }: { label: string; wert: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-default p-3">
      <div className="text-[0.75rem] font-medium text-muted">{label}</div>
      <div className="mt-1 truncate text-base font-semibold tabular-nums text-primary">{wert}</div>
    </div>
  );
}
